import { useExplore } from '@/stores/explore';
import { cn } from '@/utils/classname';
import {
  areaToString,
  lengthToString,
  volumeToString,
  Volumetrics,
} from '@skand/viewer-component-v2';
import { useMemo } from 'react';
import { getMeasurementDifferences } from '../AnnotationDetails/utils';

interface VolumetricsViewProps {
  volumetrics: Volumetrics;
}

interface ItemProps {
  label: string;
  value: string;
}

export const VolumetricsView = ({ volumetrics }: VolumetricsViewProps) => {
  const measurementUnit = useExplore(state => state.measurementUnit);
  const annotationOriginal = useExplore(state => state.annotationOriginal);
  const annotationDraft = useExplore(state => state.annotationDraft);

  const enabledInfoAlert = useMemo(() => {
    if (annotationDraft?.annotation3d && annotationOriginal?.annotation3d) {
      return getMeasurementDifferences(
        annotationDraft.annotation3d,
        annotationOriginal.annotation3d,
      );
    } else {
      return false;
    }
  }, [annotationDraft, annotationOriginal]);

  const Item = ({ label, value }: ItemProps) => (
    <div className="w-full flex flex-row justify-between">
      <p className="text-neutral-600 typo-text-s">{label}</p>
      <p className="text-neutral-800 typo-text-s">{value}</p>
    </div>
  );

  // Compute elevation after leveling
  const elevationAfterLeveling = useMemo(() => {
    const levelHeightAboveBase = volumetrics.cut / volumetrics.projectedArea;
    return lengthToString(volumetrics.minElevation + levelHeightAboveBase, measurementUnit, 4);
  }, [measurementUnit, volumetrics]);

  return (
    <div>
      <div
        className={cn(
          'flex',
          'justify-between',
          'items-center',
          'border-neutral-300',
          'border-[1px]',
          'border-solid',
          'bg-neutral-200',
          'p-3',
          'rounded-md',
          'mt-3',
          'flex-col',
          'gap-1',
          'skand-rnd-disable',
        )}
      >
        <Item
          label="Area (2D projection)"
          value={areaToString(volumetrics.projectedArea, measurementUnit, 4)}
        />
        <Item
          label="Surface Area"
          value={areaToString(volumetrics.surfaceArea, measurementUnit, 4)}
        />

        <Item label="Cut Volume" value={volumeToString(volumetrics.cut, measurementUnit, 4)} />

        <Item
          label="Fill Volume"
          value={volumeToString(Math.abs(volumetrics.fill), measurementUnit, 4)}
        />

        <Item label="Elevation after leveling" value={elevationAfterLeveling} />

        <Item
          label="Total"
          value={volumeToString(volumetrics.cut + Math.abs(volumetrics.fill), measurementUnit, 4)}
        />
      </div>
      {enabledInfoAlert && (
        <div
          className={cn(
            'w-full',
            'mt-3',
            'px-3',
            'h-[36px]',
            'mb-3',
            'bg-info-100',
            'border',
            'border-info-400',
            'border-solid',
            'rounded',
            'flex',
            'flex-row',
            'pointer-events-none',
          )}
        >
          <div className="flex items-center gap-[14px]">
            <div className="i-skand-info text-[12px] color-info-400" />
            <div className="flex items-center">
              <p className="color-neutral-800 typo-text-s">
                Want to keep these measurements? Just click
              </p>
              <p className="ml-1 color-neutral-800 typo-text-s-em">Update.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
